/* In-Stock Inventory Report — async CSV generation (task → poll → download).
 * The downloadable form of the Inventory screen's warehouse stock list: one row
 * per lot for the whole warehouse, not just the page on screen.
 *
 *   POST v1/reports/in-stock-inventory/export  { warehouseId, search }
 *        -> { taskId, reportType, status: "PENDING", fileName, ... }
 *   GET  v1/reports/tasks/{taskId}             -> poll until COMPLETED / FAILED
 *   GET  v1/reports/tasks/{taskId}/download    -> CSV blob
 *
 * Polling and download are report-type-agnostic on the backend, so they come
 * from reportApiShared rather than being repeated here. */
import { httpSupplier } from "../../HttpCommonALL";
import { unwrap, rethrow, normalizeTask, reportTasks, xUser } from "./reportApiShared";

const BASE = "v1/reports/in-stock-inventory";

const inventoryReportService = {
  /* POST v1/reports/in-stock-inventory/export -> start CSV generation.

     `warehouseId` is DROPPED when blank rather than sent empty — an absent one
     means every store, which is how a pharmacy head on "All stores" exports the
     whole group. Sending "" would read as a real filter and match nothing (same
     rule as v1/sales).

     `search` is the same free-text filter the paged list takes (medicine code /
     name / batch no / category) and is omitted when blank so the export covers
     the entire stock. Returns the normalized task so the caller can poll by
     taskId and name the download with the server-provided fileName. */
  requestExport({ warehouseId, search } = {}) {
    const body = {};
    if (warehouseId) body.warehouseId = warehouseId;
    const term = (search || "").trim();
    if (term) body.search = term;
    return httpSupplier
      .post(`${BASE}/export`, body, { headers: xUser() })
      .then((r) => normalizeTask(unwrap(r)))
      .catch(rethrow);
  },

  /* GET v1/reports/tasks/{taskId} -> the task object, polled until COMPLETED / FAILED. */
  exportStatus: (taskId) => reportTasks.status(taskId),

  /* GET v1/reports/tasks/{taskId}/download -> CSV blob; saves to disk. */
  download: (taskId, filename = "in-stock-inventory.csv") => reportTasks.download(taskId, filename),
};

export default inventoryReportService;
